import { get, isEmpty, isNil } from "lodash";
import { RECOMMENDED_POSITION } from "shared/components/ScreenRenderer/dataSources";
import { BINDING_KINDS, SLOT_TYPES } from "./workshop-view";

export const GUIDE_KINDS = { LIST: "list", CARD: "card" };

export const GUIDE_STEPS = { SOURCE: "source", ZONES: "zones", DONE: "done" };

export const CARD_ENTITY = "advanced";

export const CARD_SOURCES = [
  {
    key: "selectedPlan",
    fields: [
      "selectedPlanName",
      "selectedPlanAlias",
      "selectedPlanPrice",
      "selectedPlanBasePrice",
      "appliedDiscountCode",
      "includedGuaranteesCount",
    ],
  },
  {
    key: "insuredPeople",
    fields: ["insuredCount", "insuredExpected", "insuredSummary"],
  },
];

const SLOT_ATTRIBUTE = "data-kayros-slot";
const ITEM_SUFFIX = "_item";
const SLOT_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
const CARD_MIN_TEXTS = 2;
const CARD_MAX_SLOTS = 12;
const NOT_CARD_TYPES = [SLOT_TYPES.INPUT, SLOT_TYPES.LIST];

export const parseScreen = (html) => {
  if (isEmpty(html) || typeof DOMParser === "undefined") return null;
  return new DOMParser().parseFromString(html, "text/html");
};

const slotIndex = (slots) => {
  const byId = {};
  for (const slot of slots || []) byId[slot.id] = slot;
  return byId;
};

const slotElement = (doc, id) =>
  SLOT_ID_PATTERN.test(String(id || ""))
    ? doc.querySelector(`[${SLOT_ATTRIBUTE}="${id}"]`)
    : null;

const idOf = (element) => element.getAttribute(SLOT_ATTRIBUTE);

const slotIdsWithin = (element) =>
  Array.from(element.querySelectorAll(`[${SLOT_ATTRIBUTE}]`)).map(idOf);

export const elementPath = (element) => {
  const path = [];
  let node = element;
  while (node && node.parentElement) {
    path.unshift(
      Array.prototype.indexOf.call(node.parentElement.children, node),
    );
    node = node.parentElement;
  }
  return path;
};

const elementAt = (doc, path) => {
  let node = doc.documentElement;
  for (const index of path || []) {
    if (isNil(node)) return null;
    node = node.children[index] || null;
  }
  return node;
};

const baseIdOf = (slotId) =>
  String(slotId).endsWith(ITEM_SUFFIX)
    ? String(slotId).slice(0, -ITEM_SUFFIX.length)
    : String(slotId);

const listElementAround = (doc, byId, element) => {
  let node = element;
  while (node && node.getAttribute) {
    if (get(byId[idOf(node)], "type") === SLOT_TYPES.LIST) return node;
    node = node.parentElement;
  }
  return null;
};

const listElementOf = (doc, byId, slotId) => {
  const element = slotElement(doc, baseIdOf(slotId));
  return element ? listElementAround(doc, byId, element) : null;
};

const textSlotsWithin = (element, byId) =>
  slotIdsWithin(element)
    .map((id) => byId[id])
    .filter((slot) => get(slot, "type") === SLOT_TYPES.TEXT);

const isCard = (doc, byId, element) => {
  if (!element || element === doc.body || element === doc.documentElement)
    return false;
  if (listElementAround(doc, byId, element)) return false;
  const inside = slotIdsWithin(element).map((id) => byId[id]);
  if (inside.length > CARD_MAX_SLOTS) return false;
  if (inside.some((slot) => NOT_CARD_TYPES.includes(get(slot, "type"))))
    return false;
  return textSlotsWithin(element, byId).length >= CARD_MIN_TEXTS;
};

const containsAll = (node, elements) =>
  elements.every((element) => node.contains(element));

const commonAncestor = (elements) => {
  let node = elements[0] || null;
  while (node && !containsAll(node, elements)) node = node.parentElement;
  return node;
};

const zoneSlotsOf = (doc, byId, kind, element) => {
  if (kind === GUIDE_KINDS.CARD)
    return textSlotsWithin(element, byId).filter((slot) =>
      isNil(slot.collection),
    );
  const template = element.querySelector(
    `[${SLOT_ATTRIBUTE}="${idOf(element)}${ITEM_SUFFIX}"]`,
  );
  if (isNil(template)) return [];
  return slotIdsWithin(template)
    .map((id) => byId[id])
    .filter((slot) => !isNil(slot) && slot.type !== SLOT_TYPES.LIST);
};

export const cardSourceOf = (zones) => {
  const paths = zones
    .map((slot) => get(slot, "binding.source"))
    .filter((source) => String(source || "").startsWith(`${CARD_ENTITY}.`))
    .map((source) => source.slice(CARD_ENTITY.length + 1));
  const source = CARD_SOURCES.find((item) =>
    paths.some((path) => item.fields.includes(path)),
  );
  return source ? source.key : null;
};

export const listCollectionOf = (listSlot) =>
  get(listSlot, "binding.kind") === BINDING_KINDS.DATA
    ? get(listSlot, "binding.source") || null
    : null;

const guideStart = (doc, byId, kind, element, slotId) => {
  const zones = zoneSlotsOf(doc, byId, kind, element);
  const zone = zones.findIndex((slot) => slot.id === slotId);
  const ready =
    kind === GUIDE_KINDS.LIST
      ? !isNil(listCollectionOf(byId[idOf(element)]))
      : !isNil(cardSourceOf(zones));
  const step = () => {
    if (!ready) return GUIDE_STEPS.SOURCE;
    return zone >= 0 ? GUIDE_STEPS.ZONES : GUIDE_STEPS.DONE;
  };
  return {
    kind,
    path: elementPath(element),
    step: step(),
    zone: Math.max(zone, 0),
    source: kind === GUIDE_KINDS.CARD ? cardSourceOf(zones) : null,
  };
};

export const guideForSlot = ({ html, slots, slotId }) => {
  const doc = parseScreen(html);
  if (isNil(doc)) return null;
  const byId = slotIndex(slots);
  const list = listElementOf(doc, byId, slotId);
  return list ? guideStart(doc, byId, GUIDE_KINDS.LIST, list, slotId) : null;
};

export const guideForGroup = ({ html, slots, slotIds, slotId = null }) => {
  const doc = parseScreen(html);
  if (isNil(doc)) return null;
  const byId = slotIndex(slots);
  const lists = (slotIds || []).map((slotId) =>
    listElementOf(doc, byId, slotId),
  );
  if (lists.length && lists[0] && lists.every((list) => list === lists[0]))
    return guideStart(doc, byId, GUIDE_KINDS.LIST, lists[0], slotId);
  const elements = (slotIds || [])
    .map((slotId) => slotElement(doc, slotId))
    .filter(Boolean);
  if (!elements.length) return null;
  const card = commonAncestor(elements);
  return isCard(doc, byId, card)
    ? guideStart(doc, byId, GUIDE_KINDS.CARD, card, slotId)
    : null;
};

export const resolveGuide = ({ html, slots, guide }) => {
  if (isNil(guide)) return null;
  const doc = parseScreen(html);
  const element = doc ? elementAt(doc, guide.path) : null;
  const byId = slotIndex(slots);
  const valid =
    !isNil(element) &&
    (guide.kind === GUIDE_KINDS.LIST
      ? get(byId[idOf(element) || ""], "type") === SLOT_TYPES.LIST
      : isCard(doc, byId, element));
  if (!valid) return { ...guide, missing: true, zones: [] };
  const zones = zoneSlotsOf(doc, byId, guide.kind, element);
  const listSlot = guide.kind === GUIDE_KINDS.LIST ? byId[idOf(element)] : null;
  return {
    ...guide,
    missing: false,
    listSlot,
    collection: listCollectionOf(listSlot),
    recommendedCenter:
      guide.kind === GUIDE_KINDS.LIST &&
      element.getAttribute(RECOMMENDED_POSITION.ATTRIBUTE) ===
        RECOMMENDED_POSITION.CENTER,
    zones,
    zone: Math.min(guide.zone, Math.max(zones.length - 1, 0)),
  };
};
